import { useRef, useLayoutEffect, useEffect, ReactNode } from 'react';
import { measureSlotWidths } from '../__utils';

/**
 * Manages DOM measurements for all input slot elements (prefix, suffix, icons, etc.)
 * and applies them as CSS custom properties (--input-start-slot-width, --input-end-slot-width).
 *
 * This hook runs in two phases:
 * 1. useLayoutEffect (immediate): Measures widths synchronously before paint for accurate initial layout
 * 2. useEffect (debounced 100ms): Re-measures after paint for responsive updates when window resizes
 *
 * The CSS grid layout uses these measurements to calculate dynamic padding and spacing.
 * Example: if a prefix is 60px wide, the grid adjusts input padding to align with it.
 *
 * @param children - Input's children to detect slot changes and trigger re-measurement
 * @returns Object containing all refs needed to attach to slot DOM elements
 */
export const useInputSlotWidths = (children: ReactNode) => {
  // Refs for the wrapper container and each possible slot element
  const wrapperRef = useRef<HTMLDivElement>(null); // Main input wrapper
  const startSlotRef = useRef<HTMLDivElement>(null); // Custom slot at start
  const endSlotRef = useRef<HTMLDivElement>(null); // Custom slot at end
  const prefixRef = useRef<HTMLDivElement>(null); // Text-only prefix (e.g., "$")
  const suffixRef = useRef<HTMLDivElement>(null); // Text-only suffix (e.g., ".00")
  const startSelectRef = useRef<HTMLDivElement>(null); // Dropdown at start
  const endSelectRef = useRef<HTMLDivElement>(null); // Dropdown at end
  const startIconRef = useRef<HTMLDivElement>(null); // Icon at start
  const endIconRef = useRef<HTMLDivElement>(null); // Icon at end

  // Phase 1: Immediate measurement before browser paint
  // This ensures layout calculations are done synchronously without visual flicker
  useLayoutEffect(() => {
    measureSlotWidths(wrapperRef, {
      startSlotRef,
      endSlotRef,
      prefixRef,
      suffixRef,
      startSelectRef,
      endSelectRef,
      startIconRef,
      endIconRef,
    });
  }, [children]); // Re-measure when children change (slots added/removed)

  // Phase 2: Debounced measurement after paint
  // Runs 100ms after children change to catch responsive layout changes (window resize, etc.)
  // Debounce prevents excessive DOM measurements during rapid updates
  useEffect(() => {
    const timer = setTimeout(() => {
      measureSlotWidths(wrapperRef, {
        startSlotRef,
        endSlotRef,
        prefixRef,
        suffixRef,
        startSelectRef,
        endSelectRef,
        startIconRef,
        endIconRef,
      });
    }, 100); // 100ms debounce

    return () => clearTimeout(timer);
  }, [children]);

  return {
    wrapperRef,
    startSlotRef,
    endSlotRef,
    prefixRef,
    suffixRef,
    startSelectRef,
    endSelectRef,
    startIconRef,
    endIconRef,
  };
};
